import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Eye, EyeOff, ImagePlus, Loader2, Pencil, Trash2, X } from "lucide-react";
import { adminCreateCategory, adminDeleteCategory, adminUpdateCategory, adminUploadImage, formatApiError, getCategories } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const inputCls =
  "rounded-md border border-input bg-white/60 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary transition";

const useImageUpload = (token) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const { url } = await adminUploadImage(token, file);
      toast.success("Image uploaded");
      return url;
    } catch {
      toast.error("Upload failed — try again");
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, upload };
};

const CategoryImage = ({ image, name, className }) =>
  image ? (
    <img src={image} alt={name} className={className} />
  ) : (
    <span className={`${className} bg-primary/10 text-primary font-serif font-semibold flex items-center justify-center`}>
      {name.charAt(0)}
    </span>
  );

export const CategoriesTab = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [newName, setNewName] = useState("");
  const [newImage, setNewImage] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editImage, setEditImage] = useState("");
  const [busy, setBusy] = useState(false);
  const addFileRef = useRef(null);
  const editFileRef = useRef(null);
  const addUpload = useImageUpload(token);
  const editUpload = useImageUpload(token);

  const load = useCallback(async () => {
    try {
      setCategories(await getCategories());
    } catch {
      toast.error("Could not load categories");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminCreateCategory(token, { name: newName, image: newImage || null });
      toast.success(`Category "${newName}" created`);
      setNewName("");
      setNewImage("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (cat) => {
    setBusy(true);
    try {
      await adminUpdateCategory(token, cat.id, { name: editName, image: editImage || null, hidden: cat.hidden || false });
      toast.success(cat.name !== editName ? `Renamed — all products moved to "${editName}"` : "Category updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleHidden = async (cat) => {
    try {
      await adminUpdateCategory(token, cat.id, { name: cat.name, image: cat.image || null, hidden: !cat.hidden });
      toast.success(cat.hidden ? `"${cat.name}" is visible on the store again` : `"${cat.name}" hidden from the store`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await adminDeleteCategory(token, cat.id);
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8" data-testid="categories-tab">
      <form onSubmit={create} className="bg-white/70 border border-border/60 rounded-xl p-6 h-fit" data-testid="category-form">
        <p className="font-serif text-lg font-semibold">New category</p>
        <div className="space-y-3 mt-4">
          <input
            data-testid="category-name-input"
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Spiral Notebooks"
            className={`${inputCls} w-full`}
          />
          <div className="flex items-center gap-2">
            <input
              ref={addFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (f) {
                  const url = await addUpload.upload(f);
                  if (url) setNewImage(url);
                }
                e.target.value = "";
              }}
              data-testid="category-image-input"
            />
            {newImage && <img src={newImage} alt="" className="w-9 h-9 rounded-lg object-cover border border-border" />}
            <button
              type="button"
              data-testid="category-image-upload"
              onClick={() => addFileRef.current?.click()}
              disabled={addUpload.busy}
              className="rounded-md border border-border bg-secondary/60 px-3 py-2 text-xs font-bold flex items-center gap-1.5 hover:bg-secondary transition disabled:opacity-60"
            >
              {addUpload.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
              {newImage ? "Change photo" : "Add photo (optional)"}
            </button>
          </div>
          <button
            data-testid="category-create-button"
            disabled={busy}
            className="w-full rounded-full bg-primary text-primary-foreground py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create category
          </button>
          <p className="text-[11px] text-muted-foreground">Assign products to it from the Products tab.</p>
        </div>
      </form>

      <div className="lg:col-span-2 space-y-3" data-testid="categories-list">
        {categories.map((c) => (
          <div key={c.id} data-testid={`category-${c.id}`} className="bg-white/70 border border-border/60 rounded-xl p-4 flex items-center gap-4">
            <CategoryImage image={editing === c.id ? editImage : c.image} name={c.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
            {editing === c.id ? (
              <>
                <input
                  data-testid="category-edit-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className={`${inputCls} flex-1`}
                />
                <input
                  ref={editFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      const url = await editUpload.upload(f);
                      if (url) setEditImage(url);
                    }
                    e.target.value = "";
                  }}
                />
                <button type="button" onClick={() => editFileRef.current?.click()} className="p-2.5 rounded-full border border-border hover:bg-secondary transition-colors" aria-label="Change photo">
                  {editUpload.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                </button>
                <button data-testid="category-save-edit" onClick={() => saveEdit(c)} disabled={busy} className="p-2.5 rounded-full bg-primary text-primary-foreground" aria-label="Save">
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => setEditing(null)} className="p-2.5 rounded-full border border-border hover:bg-secondary transition-colors" aria-label="Cancel">
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <p className="font-serif font-semibold truncate">
                    {c.name}
                    {c.hidden && (
                      <span data-testid={`category-hidden-badge-${c.id}`} className="ml-2 align-middle text-[9px] uppercase tracking-[0.15em] font-bold bg-amber-100 text-amber-800 rounded-full px-2.5 py-0.5">
                        Hidden
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {c.product_count} product{c.product_count === 1 ? "" : "s"}
                    {(c.product_count || 0) === 0 && !c.hidden && " · auto-hidden on store (empty)"}
                    {c.hidden && " · hidden from store"}
                  </p>
                </div>
                <button
                  data-testid={`category-toggle-${c.id}`}
                  onClick={() => toggleHidden(c)}
                  className={`p-2.5 rounded-full border transition-colors ${c.hidden ? "border-amber-500/40 text-amber-700 hover:bg-amber-50" : "border-border hover:bg-secondary"}`}
                  aria-label={c.hidden ? `Show ${c.name}` : `Hide ${c.name}`}
                  title={c.hidden ? "Show on store" : "Hide from store"}
                >
                  {c.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  data-testid={`category-edit-${c.id}`}
                  onClick={() => { setEditing(c.id); setEditName(c.name); setEditImage(c.image || ""); }}
                  className="p-2.5 rounded-full border border-border hover:bg-secondary transition-colors"
                  aria-label={`Edit ${c.name}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`category-delete-${c.id}`}
                  onClick={() => remove(c)}
                  className="p-2.5 rounded-full border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                  aria-label={`Delete ${c.name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        ))}
        <p className="text-[11px] text-muted-foreground pt-2">Renaming a category automatically updates every product in it. Categories in use can't be deleted — reassign their products first.</p>
      </div>
    </div>
  );
};
